import { useRef, useCallback, useMemo } from 'react';
import AsyncSelect from 'react-select/async';
import { selectStyles } from './selectStyles';

export interface SelectOption<V extends number | string = number> {
  value: V;
  label: string;
}

interface AsyncSearchSelectProps<V extends number | string, O extends SelectOption<V>> {
  value: O | null;
  onChange: (option: O | null) => void;
  loadOptions: (search: string) => Promise<O[]>;
  placeholder?: string;
  isError?: boolean;
  isDisabled?: boolean;
  /** Load initial options on mount without typing. Default: true */
  defaultOptions?: boolean;
  noOptionsMessage?: string;
  debounceMs?: number;
}

function AsyncSearchSelect<V extends number | string, O extends SelectOption<V>>({
  value,
  onChange,
  loadOptions,
  placeholder = 'Type to search...',
  isError = false,
  isDisabled = false,
  defaultOptions = true,
  noOptionsMessage = 'No results',
  debounceMs = 300,
}: AsyncSearchSelectProps<V, O>) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Wrap loadOptions with debounce using the callback form that react-select supports
  const debouncedLoad = useCallback(
    (inputValue: string, callback: (options: O[]) => void) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        try {
          const options = await loadOptions(inputValue);
          callback(options);
        } catch {
          callback([]);
        }
      }, debounceMs);
    },
    [loadOptions, debounceMs]
  );

  const styles = useMemo(() => selectStyles<O>({ isError, isDisabled }), [isError, isDisabled]);

  return (
    <AsyncSelect<O, false>
      value={value}
      onChange={(val) => onChange(val)}
      loadOptions={debouncedLoad}
      defaultOptions={defaultOptions}
      cacheOptions
      placeholder={placeholder}
      isDisabled={isDisabled}
      isClearable
      styles={styles}
      menuPortalTarget={document.body}
      noOptionsMessage={() => noOptionsMessage}
      loadingMessage={() => 'Searching...'}
    />
  );
}

export default AsyncSearchSelect;
